import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../services/general/alert.service';
import { SurveyReportService } from '../../services/survey-report.service';
import {
  DEFAULT_SURVEY_REPORT_OPTIONS,
  SurveyReportAiType,
  SURVEY_REPORT_ACCENT_COLORS,
  SurveyReportAccentColor,
  SurveyReportAiStatus,
  SurveyReportAnalysisStatus,
  SurveyReportMode,
  SurveyReportOptions,
  SurveyReportQuestionStatus,
  SurveyReportRevisionMode,
  SurveyReportRevisionQuestion,
  SurveyReportRevisionState,
  SurveyReportRevisionStatus,
} from '../../models/survey-report.model';

const DEFAULT_SELECTED_STATUSES: SurveyReportAiStatus[] = ['missing', 'failed', 'outdated'];
const REGENERABLE_STATUSES: SurveyReportAiStatus[] = ['completed', 'outdated'];

type BooleanOption =
  | 'include_overview'
  | 'include_section_status'
  | 'include_index'
  | 'include_decisions'
  | 'include_section_divider'
  | 'include_question_numbers'
  | 'include_descriptions'
  | 'include_additional_charts'
  | 'include_slide_numbers';

@Component({
  selector: 'app-modal-survey-report',
  templateUrl: './modal-survey-report.component.html',
  styleUrls: ['./modal-survey-report.component.scss'],
  standalone: false,
})
export class ModalSurveyReportComponent {
  readonly summaryStatuses: SurveyReportAiStatus[] = ['completed', 'outdated', 'missing', 'failed', 'processing', 'no_answers'];
  readonly accentColors = SURVEY_REPORT_ACCENT_COLORS;
  readonly aiTypes: SurveyReportAiType[] = ['supershort', 'short', 'full'];
  analysis: SurveyReportAnalysisStatus;
  loadingAnalysis = false;
  private layoutChosenByUser = false;
  readonly slideOptions: BooleanOption[] = [
    'include_overview',
    'include_section_status',
    'include_index',
    'include_decisions',
    'include_section_divider',
    'include_question_numbers',
    'include_descriptions',
    'include_additional_charts',
    'include_slide_numbers',
  ];

  readonly revisionStatuses: SurveyReportRevisionState[] = ['saved', 'ai_ready', 'processing', 'failed', 'missing'];
  revision: SurveyReportRevisionStatus | null = null;
  loadingRevisions = false;
  revisionMode: SurveyReportRevisionMode = 'now';
  selectedRevisionIds = new Set<number>();

  options: SurveyReportOptions;
  mode: SurveyReportMode;
  selectableQuestions: SurveyReportQuestionStatus[];
  selectedIds = new Set<number>();
  availableTypologies: { typology: string; count: number }[];
  selectedTypologies: Set<string>;

  constructor(
    public dialogRef: MatDialogRef<ModalSurveyReportComponent>,
    private surveyReportService: SurveyReportService,
    private alertService: AlertService,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      analysis: SurveyReportAnalysisStatus;
      surveyId: number | string;
      surveyTitle?: string;
      previousOptions?: SurveyReportOptions | null;
    },
  ) {
    this.options = { ...DEFAULT_SURVEY_REPORT_OPTIONS, ...(data.previousOptions || {}) };
    this.layoutChosenByUser = !!data.previousOptions;
    this.analysis = data.analysis;

    const typologyCounts = new Map<string, number>();
    data.analysis.questions.forEach((question) => typologyCounts.set(question.typology, (typologyCounts.get(question.typology) || 0) + 1));
    this.availableTypologies = Array.from(typologyCounts, ([typology, count]) => ({ typology, count }));
    const previousTypologies = (this.options.typologies || []).filter((typology) => typologyCounts.has(typology));
    this.selectedTypologies = new Set(previousTypologies.length ? previousTypologies : Array.from(typologyCounts.keys()));

    this.applyAnalysis(data.analysis);
    this.loadRevisions();
  }

  private loadRevisions(): void {
    this.loadingRevisions = true;
    this.surveyReportService.revisionStatus(this.data.surveyId, (revision) => {
      this.loadingRevisions = false;
      this.revision = revision;
      this.selectedRevisionIds = new Set(this.generatableRevisions.map((question) => question.id));
      this.revisionMode = this.revisionsPending ? 'missing' : 'now';
    }, () => {
      this.loadingRevisions = false;
      this.revision = null;
    });
  }

  get revisionQuestions(): SurveyReportRevisionQuestion[] {
    return (this.revision?.questions || []).filter((question) => this.selectedTypologies.has(question.typology));
  }

  revisionCount(status: SurveyReportRevisionState): number {
    return this.revisionQuestions.filter((question) => question.revision_status === status).length;
  }

  get generatableRevisions(): SurveyReportRevisionQuestion[] {
    return this.revisionQuestions.filter((question) => question.revision_status === 'missing' || question.revision_status === 'failed');
  }

  get revisionsPending(): boolean {
    return this.generatableRevisions.length + this.revisionCount('processing') > 0;
  }

  get selectableRevisions(): SurveyReportRevisionQuestion[] {
    return this.revisionQuestions.filter(
      (question) => !question.in_flight && ['missing', 'failed', 'ai_ready'].includes(question.revision_status),
    );
  }

  get regenerableRevisions(): SurveyReportRevisionQuestion[] {
    return this.selectableRevisions.filter((question) => question.revision_status === 'ai_ready');
  }

  get revisionChoicesAvailable(): boolean {
    return this.selectableRevisions.length + this.revisionCount('processing') > 0;
  }

  get effectiveRevisionMode(): SurveyReportRevisionMode {
    if (!this.options.include_revised_statements || !this.revisionChoicesAvailable) {
      return 'now';
    }
    if (this.revisionMode === 'missing' && !this.revisionsPending) {
      return 'now';
    }
    if (this.revisionMode === 'regenerate' && this.regenerableRevisions.length + this.generatableRevisions.length === 0) {
      return 'now';
    }
    return this.revisionMode;
  }

  get allRevisionsSelected(): boolean {
    return this.selectableRevisions.length > 0 && this.selectableRevisions.every((question) => this.selectedRevisionIds.has(question.id));
  }

  get selectedRevisionCount(): number {
    return this.selectableRevisions.filter((question) => this.selectedRevisionIds.has(question.id)).length;
  }

  get revisionRegenerationCount(): number {
    switch (this.effectiveRevisionMode) {
      case 'regenerate':
        return this.regenerableRevisions.length;
      case 'selected':
        return this.regenerableRevisions.filter((question) => this.selectedRevisionIds.has(question.id)).length;
      default:
        return 0;
    }
  }

  toggleRevision(id: number): void {
    if (this.selectedRevisionIds.has(id)) {
      this.selectedRevisionIds.delete(id);
    } else {
      this.selectedRevisionIds.add(id);
    }
  }

  toggleAllRevisions(): void {
    if (this.allRevisionsSelected) {
      this.selectedRevisionIds.clear();
    } else {
      this.selectableRevisions.forEach((question) => this.selectedRevisionIds.add(question.id));
    }
  }

  get counts() {
    return this.analysis.counts;
  }

  get canChooseLayout(): boolean {
    return this.options.ai_analysis_type !== 'supershort';
  }

  setAiType(type: SurveyReportAiType): void {
    if (this.options.ai_analysis_type === type) {
      return;
    }
    this.options.ai_analysis_type = type;
    if (type === 'supershort') {
      this.options.ai_analysis_layout = 'side';
    } else if (!this.layoutChosenByUser) {
      this.options.ai_analysis_layout = type === 'full' ? 'separate' : 'side';
    }
    this.loadingAnalysis = true;
    this.surveyReportService.analysisStatus(this.data.surveyId, type, (analysis) => {
      this.loadingAnalysis = false;
      if (this.options.ai_analysis_type === type) {
        this.applyAnalysis(analysis);
      }
    }, () => this.loadingAnalysis = false);
  }

  setLayout(layout: 'side' | 'separate'): void {
    this.options.ai_analysis_layout = layout;
    this.layoutChosenByUser = true;
  }

  private applyAnalysis(analysis: SurveyReportAnalysisStatus): void {
    this.analysis = analysis;
    this.selectableQuestions = analysis.questions.filter((question) => !['no_answers', 'processing'].includes(question.ai_status));
    this.selectedIds = new Set(
      this.selectableQuestions.filter((question) => DEFAULT_SELECTED_STATUSES.includes(question.ai_status)).map((question) => question.id),
    );
    this.mode = this.missingCount > 0 ? 'missing' : this.outdatedCount > 0 ? 'update' : 'now';
  }

  get missingCount(): number {
    return (this.counts.missing || 0) + (this.counts.failed || 0);
  }

  get outdatedCount(): number {
    return this.counts.outdated || 0;
  }

  get processingCount(): number {
    return this.counts.processing || 0;
  }

  get analysesIncomplete(): boolean {
    return this.missingCount + this.outdatedCount + this.processingCount > 0;
  }

  get allSelected(): boolean {
    return this.selectableQuestions.length > 0 && this.selectableQuestions.every((question) => this.selectedIds.has(question.id));
  }

  get selectedAnalysesCount(): number {
    return this.selectableQuestions.filter((question) => this.selectedIds.has(question.id)).length;
  }

  get regenerableAnalysesCount(): number {
    return this.selectableQuestions.filter((question) => REGENERABLE_STATUSES.includes(question.ai_status)).length;
  }

  get analysisRegenerationCount(): number {
    switch (this.effectiveMode) {
      case 'regenerate':
        return this.regenerableAnalysesCount;
      case 'selected':
        return this.selectableQuestions.filter((question) => question.ai_status === 'completed' && this.selectedIds.has(question.id)).length;
      default:
        return 0;
    }
  }

  get includedQuestionsCount(): number {
    return this.analysis.questions.filter(
      (question) =>
        this.selectedTypologies.has(question.typology) && !(this.options.skip_unanswered && question.ai_status === 'no_answers'),
    ).length;
  }

  get effectiveMode(): SurveyReportMode {
    if (!this.options.include_ai_analysis) {
      return 'now';
    }
    if ((this.mode === 'missing' || this.mode === 'update') && !this.analysesIncomplete) {
      return 'now';
    }
    if (this.mode === 'regenerate' && this.selectableQuestions.length === 0) {
      return 'now';
    }
    return this.mode;
  }

  get canConfirm(): boolean {
    if (this.includedQuestionsCount === 0 || this.loadingAnalysis || this.loadingRevisions) {
      return false;
    }
    if (this.effectiveRevisionMode === 'selected' && this.selectedRevisionCount === 0) {
      return false;
    }
    return this.effectiveMode !== 'selected' || this.selectedAnalysesCount > 0;
  }

  typologyKey(typology: string): string {
    return `service.survey-service.question-typologies.${typology.toLowerCase().replace(/_/g, '-')}.title`;
  }

  toggleTypology(typology: string): void {
    if (this.selectedTypologies.has(typology)) {
      if (this.selectedTypologies.size > 1) {
        this.selectedTypologies.delete(typology);
      }
    } else {
      this.selectedTypologies.add(typology);
    }
  }

  setAccent(color: SurveyReportAccentColor): void {
    this.options.accent_color = color;
  }

  toggleQuestion(id: number): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  toggleAll(): void {
    if (this.allSelected) {
      this.selectedIds.clear();
    } else {
      this.selectableQuestions.forEach((question) => this.selectedIds.add(question.id));
    }
  }

  resetOptions(): void {
    this.options = { ...DEFAULT_SURVEY_REPORT_OPTIONS };
    this.selectedTypologies = new Set(this.availableTypologies.map((entry) => entry.typology));
  }

  plainText(html: string): string {
    const element = document.createElement('div');
    element.innerHTML = html || '';
    return (element.textContent || '').trim();
  }

  confirm(): void {
    if (!this.canConfirm) {
      return;
    }
    const analyses = this.analysisRegenerationCount;
    const revisions = this.revisionRegenerationCount;
    if (analyses + revisions === 0) {
      this.close();
      return;
    }
    this.alertService.showQuestion(
      this.translateService.instant('modals.survey-report.regenerate-confirm-title'),
      this.translateService.instant('modals.survey-report.regenerate-confirm-text', { analyses, revisions }),
      () => this.close(),
    );
  }

  private close(): void {
    const allTypologies = this.selectedTypologies.size === this.availableTypologies.length;
    const mode = this.effectiveMode;
    const revisionMode = this.effectiveRevisionMode;
    this.dialogRef.close({
      mode,
      questionIds: mode === 'selected' ? this.selectableQuestions.filter((question) => this.selectedIds.has(question.id)).map((question) => question.id) : [],
      revisionMode,
      revisionQuestionIds:
        revisionMode === 'selected'
          ? this.selectableRevisions.filter((question) => this.selectedRevisionIds.has(question.id)).map((question) => question.id)
          : [],
      options: {
        ...this.options,
        title: this.options.title?.trim() || null,
        subtitle: this.options.subtitle?.trim() || null,
        footer_text: this.options.footer_text?.trim() || null,
        typologies: allTypologies ? null : Array.from(this.selectedTypologies),
        ai_analysis_layout: this.canChooseLayout ? this.options.ai_analysis_layout : 'side',
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
